import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';
import { useMusicians } from '../hooks/useMusicians';
import { useAuth } from '../context/AuthContext';
import {
  apiGetChecklists,
  apiCreateChecklist,
  apiUpdateChecklist,
  apiDeleteChecklist,
  apiDuplicateChecklist,
  apiGetTeam,
  apiSetMyChecklistItemDone,
  type ChecklistDTO,
  type ChecklistItemDTO,
  type TeamMemberDTO,
} from '../api';
import { reportSaveError } from '../lib/saveStatus';
import { useWriteGate } from '../lib/writeGate';
import { generatePdfBlobViaServer } from '../lib/pdf';
import { buildChecklistDoc } from '../lib/checklistPdf';
import PdfPreview from '../components/PdfPreview';
import PageHeader from '../components/PageHeader';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { withPageIcon } from '../lib/uiIcons';

function newItem(_order: number): ChecklistItemDTO {
  return { id: crypto.randomUUID(), text: '', assigned_to: '', done: false };
}

// Checklists V2-B grouping categories (UI values, mirrors the server allowlist).
const CATEGORIES = ['departure', 'changeover', 'soundcheck', 'loadout', 'custom'] as const;

export default function ChecklistsPage() {
  const { t } = useTranslation();
  const { activeProfileId, getProfile } = useProfileContext();
  const { musicians } = useMusicians();
  // Checklists V2-B: the signed-in user's linked team_member — owner of HIS
  // personal lists, and the identity a self-service tick is validated against.
  const { user } = useAuth();
  const myMemberId = user?.team_member_id || null;
  const [teamMembers, setTeamMembers] = useState<TeamMemberDTO[]>([]);
  // Capability-aware write gate (lot A2). `/api/bands/:id/checklists` is mounted
  // AFTER the server's global read-only guard (server/index.js:519-525; guarded
  // mount at :547) → every create/update/delete 403s for a read-only account.
  // (Conditional pre-guard mount at :466-471 exempts holders of
  // `content.checklists` — flipped in the A2-CAP pass (2026-09-25) to the exact
  // capability: a read-only member GRANTED `content.checklists` writes here, one
  // without the grant stays frozen. Never a blanket `canWrite` anymore.)
  const writeGate = useWriteGate(activeProfileId);
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !writeGate.canWriteCap('content.checklists');
  const [checklists, setChecklists] = useState<ChecklistDTO[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  // Checklists V2-B: category + scope of the NEW checklist (set in the header).
  const [newCategory, setNewCategory] = useState<string>('custom');
  const [newScope, setNewScope] = useState<'band' | 'personal'>('band');
  // Preview docDef for the selected checklist — passed to <PdfPreview> so every
  // page renders on iOS (an iframe shows only page 1). (2026-08-13)
  const [pdfDoc, setPdfDoc] = useState<TDocumentDefinitions | null>(null);

  // ---- Autosave (focus fix 2026-10-20) ----
  // Editing used to PUT + full-reload on EVERY keystroke. The server's PUT
  // DELETEs + re-INSERTs all items, so reloaded items come back with NEW ids →
  // key={item.id} changed → row remount → input blurred on each letter.
  // Now: edits stay local, a debounced save (600ms) runs in the background, and
  // the response's fresh item ids are reconciled BY POSITION onto the client's
  // items, so React keys stay stable and focus is never lost.
  const checklistsRef = useRef(checklists);
  checklistsRef.current = checklists;
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSaveId = useRef<string | null>(null);

  const saveChecklistNow = useCallback(async (id: string) => {
    if (!activeProfileId) return;
    const cl = checklistsRef.current.find((c) => c.id === id);
    if (!cl) return;
    try {
      // position: index so the server's `ORDER BY position` mirrors the client order.
      // (Local widening — server checklist_items carries a `position` column that
      // the shared ChecklistItemDTO doesn't declare; api.ts is off-limits here.)
      type ItemWithPos = ChecklistItemDTO & { position?: number };
      const payload = { ...cl, items: (cl.items || []).map((it, i) => ({ ...it, position: (it as ItemWithPos).position ?? i })) };
      const fresh = await apiUpdateChecklist(activeProfileId, id, payload);
      // Reconcile server ids by position — keep CLIENT ids as React keys.
      setChecklists((prev) => prev.map((c) => {
        if (c.id !== id) return c;
        const serverItems = fresh.items || [];
        return {
          ...c, ...fresh,
          // Merge server fields but KEEP the client id as the React key — adopting
          // the server's fresh id would remount the row and blur the input.
          items: (c.items || []).map((it, i) => ({ ...it, ...serverItems[i], id: it.id })),
        };
      }));
    } catch (err) {
      console.error('Checklist save failed:', err);
      reportSaveError('setlist', err);
    }
  }, [activeProfileId]);

  const scheduleSave = useCallback((id: string) => {
    pendingSaveId.current = id;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      const pid = pendingSaveId.current;
      pendingSaveId.current = null;
      if (pid) void saveChecklistNow(pid);
    }, 600);
  }, [saveChecklistNow]);

  // Flush a pending save when leaving the edited checklist (or unmounting) —
  // cleanup runs with the closure of the PREVIOUS selection/band, which is what
  // the pending edit belongs to.
  const flushPendingSave = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    const pid = pendingSaveId.current;
    pendingSaveId.current = null;
    if (pid) void saveChecklistNow(pid);
  }, [saveChecklistNow]);
  useEffect(() => () => flushPendingSave(), [selectedId, flushPendingSave]);

  const load = useCallback(() => {
    if (!activeProfileId) return;
    setLoading(true);
    apiGetChecklists(activeProfileId)
      .then((data) => {
        data.forEach((c) => {
          c.items = (c.items || []).map((it) => ({ ...it, done: !!it.done }));
        });
        setChecklists(data);
        setSelectedId((prev) => (prev && data.find((c) => c.id === prev)) ? prev : data[0]?.id ?? null);
      })
      .catch((e) => { console.error(e); reportSaveError('setlist', e); })
      .finally(() => setLoading(false));
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  // Team members of the band (assignee_id must reference team_members ids).
  useEffect(() => {
    if (!activeProfileId) return;
    apiGetTeam({ band: activeProfileId }).then((rows) => setTeamMembers(rows)).catch(() => setTeamMembers([]));
  }, [activeProfileId]);

  // Toggle one of MY assigned items through the self-service route (works even
  // for a non-editor member — /api/me mount, before the read-only guard).
  const toggleMyItem = async (cl: ChecklistDTO, item: ChecklistItemDTO) => {
    const nextDone = !item.done;
    setChecklists((prev) => prev.map((c) => (c.id === cl.id
      ? { ...c, items: (c.items || []).map((it) => (it.id === item.id ? { ...it, done: nextDone } : it)) }
      : c)));
    try {
      await apiSetMyChecklistItemDone(item.id, nextDone);
    } catch (e: any) {
      alert(t('checklists.updateFailed', { msg: e.message }));
      load();
    }
  };

  // Rebuild the checklist PDF preview whenever the selection or its items change.
  useEffect(() => {
    const sel = checklists.find((c) => c.id === selectedId) || null;
    if (!sel || !activeProfileId) { setPdfDoc(null); return; }
    setPdfDoc(buildChecklistDoc(getProfile(activeProfileId)?.name, sel));
  }, [selectedId, checklists, activeProfileId, getProfile]);

  const downloadPdf = async () => {
    if (!selected || !pdfDoc) return;
    try {
      const blobUrl = await generatePdfBlobViaServer(pdfDoc);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${(selected.name || 'checklist').replace(/[^\\w\\-]+/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      console.error('Checklist PDF failed:', err);
      reportSaveError('setlist', err);
    }
  };

  if (!activeProfileId) {
    return <div className="page"><div className="loading">{t('checklists.selectBandFirst')}</div></div>;
  }

  const selected = checklists.find((c) => c.id === selectedId) || null;

  const createChecklist = async () => {
    // A2-CAP: content.checklists
    if (!writeGate.canWriteCap('content.checklists')) return;
    try {
      const c = await apiCreateChecklist(activeProfileId, {
        name: name.trim() || t('checklists.newChecklistName'),
        items: [newItem(0)],
        category: newCategory,
        scope: newScope,
        // A personal list is owned by the signed-in member (server-validated).
        owner_member_id: newScope === 'personal' ? myMemberId : null,
      });
      setName('');
      await load();
      setSelectedId(c.id);
    } catch (e) { reportSaveError('checklists', e); }
  };

  const updateSelected = async (patch: Partial<ChecklistDTO>) => {
    // A2-CAP: content.checklists
    if (!writeGate.canWriteCap('content.checklists')) return;
    if (!selected) return;
    const updated = { ...selected, ...patch };
    // Focus fix: optimistic local update + debounced autosave. No `load()`
    // here — a reload during typing replaced items with fresh server ids
    // (server DELETE+INSERT) and remounted the row, blurring the input.
    setChecklists((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
    scheduleSave(updated.id);
  };

  const removeChecklist = async (id: string) => {
    // A2-CAP: content.checklists
    if (!writeGate.canWriteCap('content.checklists')) return;
    if (!(await showConfirm(t('checklists.deleteConfirm')))) return;
    try {
      await apiDeleteChecklist(activeProfileId, id);
      setSelectedId(null);
      load();
    } catch (e) { reportSaveError('checklists', e); }
  };

  const duplicateChecklist = async (id: string) => {
    // A2-CAP: content.checklists
    if (!writeGate.canWriteCap('content.checklists')) return;
    const dup = await apiDuplicateChecklist(activeProfileId, id);
    await load();
    setSelectedId(dup.id);
  };

  const addItem = () => {
    if (!selected) return;
    updateSelected({ items: [...(selected.items || []), newItem((selected.items || []).length)] });
  };

  const updateItem = (id: string, patch: Partial<ChecklistItemDTO>) => {
    if (!selected) return;
    updateSelected({ items: (selected.items || []).map((it) => (it.id === id ? { ...it, ...patch } : it)) });
  };

  const removeItem = (id: string) => {
    if (!selected) return;
    const reordered = (selected.items || [])
      .filter((it) => it.id !== id);
    updateSelected({ items: reordered });
  };

  const moveItem = (id: string, dir: -1 | 1) => {
    if (!selected) return;
    const sorted = [...(selected.items || [])];
    const idx = sorted.findIndex((it) => it.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= sorted.length) return;
    [sorted[idx], sorted[swap]] = [sorted[swap], sorted[idx]];
    updateSelected({ items: sorted });
  };

  const doneCount = (selected?.items || []).filter((it) => it.done).length;
  const totalCount = (selected?.items || []).length;
  // Available assignees: band musicians (+ crew would need crew loading; keep it
  // to musicians for now — free text if someone's not listed).
  const assignees = musicians.map((m) => m.name).filter(Boolean);

  return (
    <div className="page">
      {/* P3a — unified page header: mono title at the left, the create
          controls as the page actions at the right (same gate as before). */}
      <PageHeader
        title={withPageIcon('checklists.title', t('checklists.title'))}
        actions={
          <>
            <select
              className="form-input"
              style={{ width: 150, maxWidth: '100%' }}
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              data-checklist-new-category
              disabled={!writeGate.canWriteCap('content.checklists')}
              title={t('checklists.categoryTitle')}
            >
              {CATEGORIES.map((cat) => <option key={cat} value={cat}>{t(`checklists.category.${cat}`)}</option>)}
            </select>
            <select
              className="form-input"
              style={{ width: 130, maxWidth: '100%' }}
              value={newScope}
              onChange={(e) => setNewScope(e.target.value === 'personal' ? 'personal' : 'band')}
              data-checklist-new-scope
              disabled={!writeGate.canWriteCap('content.checklists') || !myMemberId}
              title={myMemberId ? t('checklists.scopeTitle') : t('checklists.scopeNoMember')}
            >
              <option value="band">{t('checklists.scope.band')}</option>
              <option value="personal">{t('checklists.scope.personal')}</option>
            </select>
            <input
              className="form-input"
              placeholder={t('checklists.checklistName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: 200, maxWidth: '100%' }}
              data-checklist-new-name
              disabled={
                // A2-CAP: content.checklists
                !writeGate.canWriteCap('content.checklists')
              }
            />
            <button className="btn btn-primary" onClick={createChecklist} disabled={
              // A2-CAP: content.checklists
              !writeGate.canWriteCap('content.checklists')
            }>
              {t('checklists.newChecklist')}
            </button>
          </>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      <div className="setlists-layout" style={{ gap: 20 }}>
        {/* Checklist list — P3a card stack (selected row carries `.on`). */}
        <div>
          {loading ? <div className="loading">{t('checklists.loading')}</div> : checklists.length === 0 ? (
            <p style={{ color: 'var(--text-dim)' }}>{t('checklists.noChecklists')}</p>
          ) : (
            <div className="rc-list" data-checklists-list>
              {checklists.map((c) => (
                <div
                  key={c.id}
                  className={`rc-card clickable${c.id === selectedId ? ' on' : ''}`}
                  data-checklist-item={c.id}
                  onClick={() => setSelectedId(c.id)}
                >
                  <div className="rc-card-body">
                    <div className="rc-card-name">{c.name}</div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
                      <span className="rc-chip" style={{ fontSize: '0.7rem' }}>{t(`checklists.category.${c.category || 'custom'}`)}</span>
                      {(c.scope || 'band') === 'personal' && (
                        <span className="rc-chip" style={{ fontSize: '0.7rem', opacity: 0.8 }}>{t('checklists.scope.personal')}</span>
                      )}
                    </div>
                  </div>
                  <span className="rc-chip">{(c.items || []).length}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected checklist editor */}
        <div>
          {!selected ? (
            <div className="card" style={{ textAlign: 'center', padding: 48 }}>
              <p style={{ color: 'var(--text-dim)' }}>{t('checklists.selectOrCreate')}</p>
            </div>
          ) : (
            <div className="card">
              <div className="rc-header" style={{ marginBottom: 16 }}>
                <input className="form-input" style={{ fontSize: 'var(--font-xl)', fontWeight: 600 }}
                  data-checklist-name
                  value={selected.name}
                  onChange={(e) => updateSelected({ name: e.target.value })}
                  disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}
                  placeholder={t('checklists.checklistName')} />
                <div className="rc-header-actions">
                  <span style={{ color: 'var(--text-dim)', marginRight: 8 }}>{t('checklists.doneCount', { done: doneCount, total: totalCount })}</span>
                  <button className="btn btn-secondary" onClick={downloadPdf} disabled={!pdfDoc}>{t('checklists.exportPdf')}</button>
                  <button className="btn btn-secondary" onClick={() => duplicateChecklist(selected.id)} disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}>{t('checklists.duplicate')}</button>
                  <button className="btn btn-danger" onClick={() => removeChecklist(selected.id)} disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}>{t('checklists.delete')}</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
                <button className="btn btn-primary" onClick={addItem} disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}>{t('checklists.addItem')}</button>
                {/* V2-B: category + scope of the selected checklist. */}
                <select
                  className="form-input"
                  style={{ width: 160 }}
                  value={selected.category || 'custom'}
                  onChange={(e) => updateSelected({ category: e.target.value })}
                  disabled={!writeGate.canWriteCap('content.checklists')}
                  data-checklist-category
                  title={t('checklists.categoryTitle')}
                >
                  {CATEGORIES.map((cat) => <option key={cat} value={cat}>{t(`checklists.category.${cat}`)}</option>)}
                </select>
                <select
                  className="form-input"
                  style={{ width: 140 }}
                  value={selected.scope || 'band'}
                  onChange={(e) => updateSelected({
                    scope: e.target.value === 'personal' ? 'personal' : 'band',
                    owner_member_id: e.target.value === 'personal' ? (selected.owner_member_id || myMemberId) : null,
                  })}
                  disabled={!writeGate.canWriteCap('content.checklists') || (!myMemberId && (selected.scope || 'band') !== 'personal')}
                  data-checklist-scope
                  title={t('checklists.scopeTitle')}
                >
                  <option value="band">{t('checklists.scope.band')}</option>
                  <option value="personal">{t('checklists.scope.personal')}</option>
                </select>
              </div>

              <ul style={{ listStyle: 'none', padding: 0 }}>
                {(selected.items || []).map((item) => (
                  <li key={item.id} className="rc-form-row" style={{
                    opacity: item.done ? 0.55 : 1,
                  }}>
                    <input
                      type="checkbox"
                      checked={item.done}
                      onChange={(e) => updateItem(item.id, { done: e.target.checked })}
                      disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}
                      style={{ width: 18, height: 18, flexShrink: 0 }}
                    />
                    <input className="form-input" style={{ flex: 2 }}
                      placeholder={t('checklists.taskPh')}
                      value={item.text}
                      onChange={(e) => updateItem(item.id, { text: e.target.value })}
                      disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
} />
                    <input className="form-input" style={{ flex: 1 }}
                      list="checklist-assignees"
                      placeholder={t('checklists.assigneePh')}
                      value={item.assigned_to || ''}
                      onChange={(e) => updateItem(item.id, { assigned_to: e.target.value })}
                      disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
} />
                    {/* V2-B: structured assignee (team_member id). Picking a member
                        keeps the legacy `assigned_to` text in sync for old readers. */}
                    <select
                      className="form-input"
                      style={{ flex: 1, minWidth: 120 }}
                      value={item.assignee_id || ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        const m = teamMembers.find((x) => String(x.id) === id);
                        updateItem(item.id, { assignee_id: id || null, assigned_to: m ? (m.nickname || m.name) : (item.assigned_to || '') });
                      }}
                      disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}
                      data-checklist-assignee={item.id}
                    >
                      <option value="">{t('checklists.assigneeNone')}</option>
                      {teamMembers.map((m) => (
                        <option key={m.id} value={m.id}>{m.nickname || m.name}</option>
                      ))}
                    </select>
                    <button className="btn btn-small btn-secondary" onClick={() => moveItem(item.id, -1)} disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}>↑</button>
                    <button className="btn btn-small btn-secondary" onClick={() => moveItem(item.id, 1)} disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}>↓</button>
                    <button className="btn btn-small btn-danger" onClick={() => removeItem(item.id)} disabled={
  // A2-CAP: content.checklists
  !writeGate.canWriteCap('content.checklists')
}>✕</button>
                  </li>
                ))}
              </ul>
              {pdfDoc && (
                <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <h3 style={{ marginBottom: 8 }}>{t('checklists.pdfPreview')}</h3>
                  <PdfPreview docDef={pdfDoc} height={480} />
                </div>
              )}
              <datalist id="checklist-assignees">
                {assignees.map((a) => <option key={a} value={a} />)}
              </datalist>
            </div>
          )}
        </div>
      </div>

      {/* Checklists V2-B: MY section — my personal lists + the items assigned
          to me across every visible list. Ticking runs through the self-service
          route (works even for a non-editor member). */}
      {myMemberId && (
        <div className="card" style={{ marginTop: 20 }} data-checklists-my>
          <h3 style={{ marginBottom: 8 }}>{t('checklists.myTitle')}</h3>
          <p style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', marginTop: 0 }}>{t('checklists.mySubtitle')}</p>
          <div className="rc-list">
            {checklists.filter((c) => (c.scope || 'band') === 'personal' && String(c.owner_member_id || '') === String(myMemberId)).map((c) => (
              <div key={c.id} className="rc-card clickable" onClick={() => setSelectedId(c.id)}>
                <div className="rc-card-body"><div className="rc-card-name">{c.name}</div></div>
                <span className="rc-chip">{(c.items || []).length}</span>
              </div>
            ))}
          </div>
          <h4 style={{ margin: '16px 0 8px' }}>{t('checklists.myItems')}</h4>
          {(() => {
            const mine = checklists.flatMap((c) => (c.items || [])
              .filter((it) => String(it.assignee_id || '') === String(myMemberId))
              .map((it) => ({ cl: c, it })));
            if (mine.length === 0) return <p style={{ color: 'var(--text-dim)' }}>{t('checklists.myItemsEmpty')}</p>;
            return (
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {mine.map(({ cl, it }) => (
                  <li key={`${cl.id}-${it.id}`} className="rc-form-row" style={{ opacity: it.done ? 0.55 : 1 }}>
                    <input
                      type="checkbox"
                      checked={!!it.done}
                      onChange={() => void toggleMyItem(cl, it)}
                      style={{ width: 18, height: 18, flexShrink: 0 }}
                      data-my-checklist-item={it.id}
                    />
                    <span className="rc-card-body" style={{ overflowWrap: 'anywhere' }}>{it.text || '—'}</span>
                    <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>{cl.name}</span>
                  </li>
                ))}
              </ul>
            );
          })()}
        </div>
      )}
    </div>
  );
}
